import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { BROKERS, CreateAccountSchema, UpsertHlSecurityMappingSchema } from 'shared';
import type {
  CreateAccountInput,
  NotificationPreferences,
  UpsertHlSecurityMappingInput,
} from 'shared';
import { toast } from 'sonner';

import { AccountMandateControl } from '../components/domain/AccountMandateControl';
import { AutoSyncPanel } from '../components/domain/AutoSyncPanel';
import { ConnectedAppsSection } from '../components/domain/ConnectedAppsSection';
import { NavInputDialog } from '../components/domain/NavInputDialog';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { FormField } from '../components/ui/FormField';
import { Label } from '../components/ui/Label';
import {
  useCreateAccount,
  useDeleteAccount,
  useUpdateAccount,
} from '../features/accounts/mutations';
import { useAccounts } from '../features/accounts/queries';
import { useUpdatePreferences } from '../features/auth/mutations';
import { useMe } from '../features/auth/queries';
import {
  useDeleteHlSecurityMapping,
  useUpdateHlSecurityMapping,
  useUpsertHlSecurityMapping,
} from '../features/hlSecurityMappings/mutations';
import { useHlSecurityMappings } from '../features/hlSecurityMappings/queries';
import { useDeleteImport, usePurgeAllTrades } from '../features/imports/mutations';
import { useAccountImports } from '../features/imports/queries';
import { MANDATE_OPTIONS, mandateLabel } from '../lib/accountMandate';
import type { AccountDto, HlSecurityMappingDto, ImportDto } from '../lib/api/endpoints';

function importTradeRows(importRecord: ImportDto): number {
  return importRecord.summary?.tradeRows ?? 0;
}

function importTotalRows(importRecord: ImportDto): number {
  return importRecord.summary?.totalRows ?? 0;
}

function renameErrorMessage(error: unknown): string {
  if (axios.isAxiosError<{ error?: { message?: string } }>(error)) {
    const message = error.response?.data.error?.message;
    if (message) return message;
  }
  return 'Could not rename account. Please try again.';
}

function ImportHistory({ account }: { account: AccountDto }) {
  const imports = useAccountImports(account.id);
  const deleteImport = useDeleteImport();
  const purgeAllTrades = usePurgeAllTrades();
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeText, setPurgeText] = useState('');
  const canPurge = purgeText === 'PURGE';

  function onDeleteImport(importRecord: ImportDto) {
    const trades = importTradeRows(importRecord);
    const confirmed = window.confirm(`Delete this import and its ${trades} trades?`);
    if (!confirmed) return;

    deleteImport.mutate(importRecord.id, {
      onSuccess: (result) => {
        toast.success(`Import deleted. Removed ${result.tradesRemoved} trades.`);
      },
    });
  }

  function onPurgeAllTrades() {
    purgeAllTrades.mutate(account.id, {
      onSuccess: (result) => {
        setPurgeOpen(false);
        setPurgeText('');
        toast.success(`Purged ${result.tradesRemoved} trades.`);
      },
    });
  }

  return (
    <details className="mt-4 rounded border border-border bg-surface p-3">
      <summary className="cursor-pointer text-sm font-semibold">Import History</summary>
      <div className="mt-3 space-y-4">
        {imports.isLoading ? (
          <p className="text-sm text-text-muted">Loading import history...</p>
        ) : imports.isError ? (
          <p className="text-sm text-danger">Could not load import history.</p>
        ) : imports.data && imports.data.length > 0 ? (
          <div className="overflow-x-auto rounded border border-border">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-bg-muted text-left text-xs uppercase tracking-normal text-text-muted">
                <tr>
                  <th scope="col" className="px-3 py-3">
                    Date
                  </th>
                  <th scope="col" className="px-3 py-3">
                    Filename
                  </th>
                  <th scope="col" className="px-3 py-3">
                    Rows
                  </th>
                  <th scope="col" className="px-3 py-3">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-3">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-bg">
                {imports.data.map((importRecord) => (
                  <tr key={importRecord.id}>
                    <td className="whitespace-nowrap px-3 py-3">
                      {new Date(importRecord.createdAt).toLocaleDateString()}
                    </td>
                    <td className="max-w-xs truncate px-3 py-3">{importRecord.fileName}</td>
                    <td className="whitespace-nowrap px-3 py-3 tabular-nums">
                      {importTotalRows(importRecord)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3">{importRecord.status}</td>
                    <td className="whitespace-nowrap px-3 py-3">
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        disabled={deleteImport.isPending}
                        onClick={() => onDeleteImport(importRecord)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-text-muted">No imports for this account.</p>
        )}

        <div className="rounded border border-danger/40 bg-danger-subtle p-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-red-100">Purge all trades</p>
              <p className="text-sm text-red-100/80">
                Deletes every trade for this account. Imports remain available for audit.
              </p>
            </div>
            <Button type="button" variant="destructive" onClick={() => setPurgeOpen(true)}>
              Purge all trades
            </Button>
          </div>
          {purgeOpen ? (
            <div className="mt-3 space-y-3 rounded border border-danger/50 bg-bg p-3">
              <Label htmlFor={`purge-${account.id}`}>Type PURGE to confirm</Label>
              <input
                id={`purge-${account.id}`}
                className="h-10 w-full rounded border border-border bg-bg px-3 text-sm text-text"
                value={purgeText}
                onChange={(event) => setPurgeText(event.target.value)}
              />
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button
                  type="button"
                  variant="destructive"
                  disabled={!canPurge || purgeAllTrades.isPending}
                  onClick={onPurgeAllTrades}
                >
                  Confirm purge
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setPurgeOpen(false);
                    setPurgeText('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </details>
  );
}

const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  notifications: {
    BREACH: true,
    WEEKLY_REVIEW: true,
    IMPORT_DONE: true,
    JOURNAL_REQUIRED: true,
    REVIEW_PENDING: true,
    RULE_CHANGE: true,
  },
  quietHours: { start: '22:00', end: '07:00' },
};

function NotificationPreferencesSection({ preferences }: { preferences: NotificationPreferences }) {
  const updatePreferences = useUpdatePreferences();
  const [draft, setDraft] = useState<NotificationPreferences>(preferences);

  function setNotification(kind: keyof NotificationPreferences['notifications'], value: boolean) {
    setDraft((current) => ({
      ...current,
      notifications: { ...current.notifications, [kind]: value, BREACH: true },
    }));
  }

  function save() {
    updatePreferences.mutate(
      { ...draft, notifications: { ...draft.notifications, BREACH: true } },
      {
        onSuccess: () => toast.success('Notification preferences saved.'),
      },
    );
  }

  return (
    <Card
      header={
        <div>
          <h2 className="text-lg font-semibold">Notification Preferences</h2>
          <p className="text-sm text-text-muted">In-app alerts and quiet hours.</p>
        </div>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        {(
          [
            ['BREACH', 'Breaches'],
            ['WEEKLY_REVIEW', 'Weekly review'],
            ['IMPORT_DONE', 'Import complete'],
            ['JOURNAL_REQUIRED', 'Journal required'],
            ['REVIEW_PENDING', 'Review pending'],
            ['RULE_CHANGE', 'Rule changes'],
          ] as const
        ).map(([kind, label]) => (
          <label
            key={kind}
            className="flex items-center justify-between gap-3 rounded border border-border bg-bg p-3 text-sm"
          >
            <span>
              <span className="block font-medium">{label}</span>
              {kind === 'BREACH' ? (
                <span className="text-xs text-text-muted">Always on</span>
              ) : null}
            </span>
            <input
              type="checkbox"
              checked={draft.notifications[kind]}
              disabled={kind === 'BREACH'}
              onChange={(event) => setNotification(kind, event.target.checked)}
            />
          </label>
        ))}
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="space-y-2 text-sm" htmlFor="quiet-start">
          Quiet hours start
          <input
            id="quiet-start"
            type="time"
            className="h-10 w-full rounded border border-border bg-bg px-3 text-sm text-text"
            value={draft.quietHours.start}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                quietHours: { ...current.quietHours, start: event.target.value },
              }))
            }
          />
        </label>
        <label className="space-y-2 text-sm" htmlFor="quiet-end">
          Quiet hours end
          <input
            id="quiet-end"
            type="time"
            className="h-10 w-full rounded border border-border bg-bg px-3 text-sm text-text"
            value={draft.quietHours.end}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                quietHours: { ...current.quietHours, end: event.target.value },
              }))
            }
          />
        </label>
      </div>
      <div className="mt-4">
        <Button type="button" disabled={updatePreferences.isPending} onClick={save}>
          Save notification preferences
        </Button>
      </div>
    </Card>
  );
}

function HlSecurityMappingsSection() {
  const mappings = useHlSecurityMappings();
  const upsertMapping = useUpsertHlSecurityMapping();
  const updateMapping = useUpdateHlSecurityMapping();
  const deleteMapping = useDeleteHlSecurityMapping();
  const [editingMapping, setEditingMapping] = useState<HlSecurityMappingDto | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const form = useForm<UpsertHlSecurityMappingInput>({
    resolver: zodResolver(UpsertHlSecurityMappingSchema),
    defaultValues: {
      sourceDescription: '',
      symbol: '',
    },
  });

  function resetForm() {
    setEditingMapping(null);
    form.reset({ sourceDescription: '', symbol: '' });
  }

  function editMapping(mapping: HlSecurityMappingDto) {
    setEditingMapping(mapping);
    form.reset({
      sourceDescription: mapping.sourceDescription,
      symbol: mapping.symbol,
    });
    const formElement = formRef.current;
    if (formElement && typeof formElement.scrollIntoView === 'function') {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    window.setTimeout(() => form.setFocus('symbol'), 0);
  }

  function removeMapping(mapping: HlSecurityMappingDto) {
    const confirmed = window.confirm(
      `Delete HL mapping ${mapping.normalizedName} -> ${mapping.symbol}?`,
    );
    if (!confirmed) return;

    deleteMapping.mutate(mapping.id, {
      onSuccess: () => {
        if (editingMapping?.id === mapping.id) resetForm();
        toast.success(`Deleted HL mapping ${mapping.normalizedName}.`);
      },
      onError: (err) => {
        toast.error(
          `Could not delete mapping: ${err instanceof Error ? err.message : 'Unknown error'}`,
        );
      },
    });
  }

  const onSubmit = form.handleSubmit((values) => {
    if (editingMapping) {
      updateMapping.mutate(
        { id: editingMapping.id, body: values },
        {
          onSuccess: (mapping) => {
            resetForm();
            toast.success(`Updated HL mapping ${mapping.normalizedName} -> ${mapping.symbol}.`);
          },
          onError: (err) => {
            toast.error(
              `Could not update mapping: ${err instanceof Error ? err.message : 'Unknown error'}`,
            );
          },
        },
      );
      return;
    }

    upsertMapping.mutate(values, {
      onSuccess: (mapping) => {
        resetForm();
        toast.success(`Saved HL mapping ${mapping.normalizedName} -> ${mapping.symbol}.`);
      },
      onError: (err) => {
        toast.error(
          `Could not save mapping: ${err instanceof Error ? err.message : 'Unknown error'}`,
        );
      },
    });
  });

  return (
    <Card
      header={
        <div>
          <h2 className="text-lg font-semibold">HL Security Mappings</h2>
          <p className="text-sm text-text-muted">Manual symbols for HL transaction imports.</p>
        </div>
      }
    >
      {editingMapping ? (
        <div className="mb-4 rounded border border-primary/40 bg-primary/10 p-3 text-sm">
          <p className="font-medium text-primary">Editing {editingMapping.symbol} mapping</p>
          <p className="mt-1 text-text-muted">{editingMapping.normalizedName}</p>
        </div>
      ) : null}

      <form ref={formRef} className="grid gap-4 md:grid-cols-[1fr_160px_auto]" onSubmit={onSubmit}>
        <FormField
          label="HL description"
          error={form.formState.errors.sourceDescription?.message}
          {...form.register('sourceDescription')}
        />
        <FormField
          label="Symbol"
          error={form.formState.errors.symbol?.message}
          {...form.register('symbol')}
        />
        <div className="flex items-end">
          <div className="flex w-full flex-col gap-2 sm:flex-row md:flex-col">
            <Button type="submit" disabled={upsertMapping.isPending || updateMapping.isPending}>
              {editingMapping ? 'Update mapping' : 'Add mapping'}
            </Button>
            {editingMapping ? (
              <Button type="button" variant="secondary" onClick={resetForm}>
                Cancel edit
              </Button>
            ) : null}
          </div>
        </div>
      </form>

      <div className="mt-4 overflow-x-auto rounded border border-border">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-bg-muted text-left text-xs uppercase tracking-normal text-text-muted">
            <tr>
              <th scope="col" className="px-3 py-3">
                Symbol
              </th>
              <th scope="col" className="px-3 py-3">
                Normalized name
              </th>
              <th scope="col" className="px-3 py-3">
                Source
              </th>
              <th scope="col" className="px-3 py-3">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-bg">
            {mappings.isLoading ? (
              <tr>
                <td className="px-3 py-3 text-text-muted" colSpan={4}>
                  Loading mappings...
                </td>
              </tr>
            ) : mappings.isError ? (
              <tr>
                <td className="px-3 py-3 text-danger" colSpan={4}>
                  Could not load mappings.
                </td>
              </tr>
            ) : (
              mappings.data?.slice(0, 40).map((mapping) => (
                <tr
                  className={editingMapping?.id === mapping.id ? 'bg-primary/5' : undefined}
                  key={mapping.id}
                >
                  <td className="whitespace-nowrap px-3 py-3 font-medium">{mapping.symbol}</td>
                  <td className="px-3 py-3">{mapping.normalizedName}</td>
                  <td className="max-w-sm truncate px-3 py-3" title={mapping.sourceDescription}>
                    {mapping.sourceDescription}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3">
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        aria-label={`Edit ${mapping.symbol} mapping`}
                        onClick={() => editMapping(mapping)}
                      >
                        Edit
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        aria-label={`Delete ${mapping.symbol} mapping`}
                        disabled={deleteMapping.isPending}
                        onClick={() => removeMapping(mapping)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export function Settings() {
  const me = useMe();
  const accounts = useAccounts();
  const createAccount = useCreateAccount();
  const updateAccount = useUpdateAccount();
  const deleteAccount = useDeleteAccount();
  const [renameState, setRenameState] = useState<{ accountId: string; name: string } | null>(null);
  const [renameError, setRenameError] = useState<string | null>(null);
  const form = useForm<CreateAccountInput>({
    resolver: zodResolver(CreateAccountSchema),
    defaultValues: {
      name: 'IBKR Active',
      broker: 'IBKR',
      mandate: 'SWING_TRADING',
      currency: 'USD',
    },
  });
  const createMandate = form.watch('mandate');
  const createMandateDescription = MANDATE_OPTIONS.find(
    (option) => option.value === createMandate,
  )?.description;

  const onSubmit = form.handleSubmit((values) => {
    createAccount.mutate(values, {
      onSuccess: () => form.reset(values),
    });
  });

  if (me.isLoading || accounts.isLoading) {
    return <main className="p-6 text-text-muted">Loading settings...</main>;
  }

  if (me.isError || accounts.isError || !me.data) {
    return <main className="p-6 text-danger">Could not load settings.</main>;
  }

  return (
    <main className="min-h-screen bg-bg px-4 py-6 text-text sm:px-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <header>
          <h1 className="text-2xl font-semibold">Settings</h1>
          <p className="mt-1 text-sm text-text-muted">Profile and broker accounts.</p>
        </header>

        <Card
          header={
            <div>
              <h2 className="text-lg font-semibold">Profile</h2>
              <p className="text-sm text-text-muted">Read-only for M1.</p>
            </div>
          }
        >
          <dl className="grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-text-muted">Name</dt>
              <dd className="font-medium">{me.data.name ?? 'Not set'}</dd>
            </div>
            <div>
              <dt className="text-text-muted">Email</dt>
              <dd className="font-medium">{me.data.email}</dd>
            </div>
            <div>
              <dt className="text-text-muted">Timezone</dt>
              <dd className="font-medium">{me.data.timezone}</dd>
            </div>
            <div>
              <dt className="text-text-muted">Base currency</dt>
              <dd className="font-medium">{me.data.baseCurrency}</dd>
            </div>
          </dl>
        </Card>

        <NotificationPreferencesSection
          preferences={me.data.userPreferences ?? DEFAULT_NOTIFICATION_PREFERENCES}
        />

        <ConnectedAppsSection />

        <Card
          header={
            <div>
              <h2 className="text-lg font-semibold">Market data</h2>
              <p className="text-sm text-text-muted">
                Check the Market Health pipelines and re-run any that failed overnight.
              </p>
            </div>
          }
        >
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="secondary" size="sm">
              <Link to="/settings/market-data">Manage market data</Link>
            </Button>
            <Button asChild variant="secondary" size="sm">
              <Link to="/settings/flow-etfs">Edit Flow ETF lists</Link>
            </Button>
            <Button asChild variant="secondary" size="sm">
              <Link to="/settings/stock-scan">Stock scan thresholds</Link>
            </Button>
            <Button asChild variant="secondary" size="sm">
              <Link to="/settings/stock-groups">Stock groups</Link>
            </Button>
          </div>
        </Card>

        {accounts.data
          ?.filter((account) => account.broker === 'IBKR')
          .map((account) => (
            <AutoSyncPanel key={account.id} account={account} />
          ))}

        <HlSecurityMappingsSection />

        <Card
          header={
            <div>
              <h2 className="text-lg font-semibold">Accounts</h2>
              <p className="text-sm text-text-muted">Create, rename, or remove broker accounts.</p>
            </div>
          }
        >
          <div className="mb-4">
            <Button asChild variant="secondary">
              <Link to="/import">Open import wizard</Link>
            </Button>
          </div>
          <form className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5" onSubmit={onSubmit}>
            <div className="lg:col-span-2">
              <FormField
                label="Account name"
                error={form.formState.errors.name?.message}
                {...form.register('name')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="broker">Broker</Label>
              <select
                id="broker"
                className="h-10 w-full rounded border border-border bg-bg px-3 text-sm text-text"
                {...form.register('broker')}
              >
                {BROKERS.map((broker) => (
                  <option key={broker} value={broker}>
                    {broker}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mandate">Mandate</Label>
              <select
                id="mandate"
                className="h-10 w-full rounded border border-border bg-bg px-3 text-sm text-text"
                {...form.register('mandate')}
              >
                {MANDATE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <p className="text-xs leading-5 text-text-muted">{createMandateDescription}</p>
            </div>
            <FormField
              label="Currency"
              error={form.formState.errors.currency?.message}
              {...form.register('currency')}
            />
            <div className="sm:col-span-2 lg:col-span-5">
              {createAccount.isError ? (
                <p className="mb-3 text-sm text-danger">Could not create account.</p>
              ) : null}
              <Button className="w-full sm:w-auto" type="submit" disabled={createAccount.isPending}>
                {createAccount.isPending ? 'Creating...' : 'Create account'}
              </Button>
            </div>
          </form>

          <div className="mt-6 space-y-3">
            {accounts.data && accounts.data.length > 0 ? (
              accounts.data.map((account) => (
                <div className="rounded border border-border bg-bg p-3" key={account.id}>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-medium">{account.name}</p>
                      <p className="text-sm text-text-muted">
                        {account.broker} | {mandateLabel(account.mandate)} | {account.currency}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <NavInputDialog
                        account={account}
                        baseCurrency={me.data.baseCurrency}
                        trigger={
                          <Button type="button" variant="secondary">
                            Update NAV
                          </Button>
                        }
                      />
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setRenameState({ accountId: account.id, name: account.name });
                          setRenameError(null);
                        }}
                      >
                        Rename
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={() => deleteAccount.mutate(account.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                  <AccountMandateControl account={account} />
                  {renameState?.accountId === account.id ? (
                    <form
                      className="mt-3 flex flex-col gap-3 rounded border border-border bg-surface p-3 sm:flex-row sm:items-end"
                      onSubmit={(event) => {
                        event.preventDefault();
                        const name = renameState.name.trim();
                        if (!name) {
                          setRenameError('Account name cannot be empty.');
                          return;
                        }
                        setRenameError(null);
                        updateAccount.mutate(
                          { id: account.id, input: { name } },
                          {
                            onSuccess: () => setRenameState(null),
                            onError: (error) => setRenameError(renameErrorMessage(error)),
                          },
                        );
                      }}
                    >
                      <div className="min-w-0 flex-1">
                        <FormField
                          id={`rename-${account.id}`}
                          label="New account name"
                          value={renameState.name}
                          error={renameError ?? undefined}
                          onChange={(event) => {
                            setRenameState({ accountId: account.id, name: event.target.value });
                            setRenameError(null);
                          }}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" disabled={updateAccount.isPending}>
                          {updateAccount.isPending ? 'Saving...' : 'Save name'}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() => {
                            setRenameState(null);
                            setRenameError(null);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : null}
                  <ImportHistory account={account} />
                </div>
              ))
            ) : (
              <p className="text-sm text-text-muted">No accounts yet.</p>
            )}
          </div>
        </Card>
      </div>
    </main>
  );
}
